<script setup lang="ts">
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { ContractTableDefinition, ContractTableRow, ContractTableValues } from '~/types/models';

withDefaults(defineProps<{
    tables: ContractTableDefinition[];
    disabled?: boolean;
    loading?: boolean;
    emptyText?: string;
    emptyRowText?: string;
    addRowLabel?: string;
    loadingText?: string;
    rowActionsLabel?: string;
}>(), { disabled: false, loading: false, emptyText: 'No dynamic tables are configured for this contract.', emptyRowText: 'No rows added.', addRowLabel: 'Add row', loadingText: 'Loading contract tables…', rowActionsLabel: 'Row' });

const rowsByTable = defineModel<ContractTableValues>({ default: () => ({}) });

function updateRows(tableKey: string, rows: ContractTableRow[]) {
    rowsByTable.value = { ...rowsByTable.value, [tableKey]: rows };
}

function addRow(table: ContractTableDefinition) {
    const rows = [...(rowsByTable.value[table.key] || [])];
    rows.push(Object.fromEntries(table.columns.map(column => [column.key, ''])));
    updateRows(table.key, rows);
}

function updateCell(table: ContractTableDefinition, rowIndex: number, columnKey: string, value: string) {
    const rows = [...(rowsByTable.value[table.key] || [])];
    rows[rowIndex] = { ...(rows[rowIndex] || {}), [columnKey]: value };
    updateRows(table.key, rows);
}

function removeRow(table: ContractTableDefinition, rowIndex: number) {
    updateRows(table.key, (rowsByTable.value[table.key] || []).filter((_, index) => index !== rowIndex));
}

function moveRow(table: ContractTableDefinition, rowIndex: number, direction: -1 | 1) {
    const rows = [...(rowsByTable.value[table.key] || [])];
    const targetIndex = rowIndex + direction;
    if (targetIndex < 0 || targetIndex >= rows.length) return;
    [rows[rowIndex], rows[targetIndex]] = [rows[targetIndex]!, rows[rowIndex]!];
    updateRows(table.key, rows);
}
</script>

<template>
    <section class="space-y-5">
        <p v-if="loading" class="text-sm text-muted-foreground">{{ loadingText }}</p>
        <p v-else-if="!tables.length" class="text-sm text-muted-foreground">{{ emptyText }}</p>
        <section v-for="table in tables" :key="table.key" class="space-y-3 border-t border-border/70 pt-5 first:border-0 first:pt-0">
            <div class="flex flex-wrap items-center justify-between gap-3">
                <div><h3 class="text-sm font-semibold">{{ table.label }}</h3><slot name="description" :table="table"><p class="mt-1 text-xs text-muted-foreground">Add the rows that apply to this contract. An empty table is left out of the document.</p></slot></div>
                <slot name="actions" :table="table" :add-row="() => addRow(table)"><Button type="button" size="sm" variant="outline" :disabled="disabled || loading" @click="addRow(table)"><Plus class="mr-1 size-3.5" />{{ addRowLabel }}</Button></slot>
            </div>
            <div v-if="!(rowsByTable[table.key] || []).length" class="py-2 text-sm text-muted-foreground"><slot name="empty" :table="table">{{ emptyRowText }}</slot></div>
            <div v-else class="w-full overflow-x-auto">
                <Table class="min-w-[36rem]">
                    <TableHeader><TableRow><TableHead v-for="column in table.columns" :key="column.key">{{ column.label }}</TableHead><TableHead class="w-28 text-right">{{ rowActionsLabel }}</TableHead></TableRow></TableHeader>
                    <TableBody>
                        <TableRow v-for="(row, rowIndex) in rowsByTable[table.key]" :key="rowIndex">
                            <TableCell v-for="column in table.columns" :key="column.key" class="min-w-36"><Input :model-value="row[column.key] || ''" :aria-label="`${table.label}, row ${rowIndex + 1}, ${column.label}`" :disabled="disabled || loading" @update:model-value="updateCell(table, rowIndex, column.key, String($event))" /></TableCell>
                            <TableCell class="text-right"><div class="inline-flex items-center justify-end gap-0.5"><Button type="button" variant="ghost" size="icon" class="size-8" :aria-label="`Move row ${rowIndex + 1} up in ${table.label}`" :disabled="disabled || loading || rowIndex === 0" @click="moveRow(table, rowIndex, -1)"><ArrowUp class="size-3.5" /></Button><Button type="button" variant="ghost" size="icon" class="size-8" :aria-label="`Move row ${rowIndex + 1} down in ${table.label}`" :disabled="disabled || loading || rowIndex === (rowsByTable[table.key]?.length || 0) - 1" @click="moveRow(table, rowIndex, 1)"><ArrowDown class="size-3.5" /></Button><Button type="button" variant="ghost" size="icon" class="size-8 text-muted-foreground" :aria-label="`Remove row ${rowIndex + 1} from ${table.label}`" :disabled="disabled || loading" @click="removeRow(table, rowIndex)"><Trash2 class="size-3.5" /></Button></div></TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        </section>
    </section>
</template>
